import { useEffect } from 'react';
import type { RefObject } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export type InitialFocusTarget = 'first' | 'close' | RefObject<HTMLElement>;

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => !element.hasAttribute('aria-hidden') && element.offsetParent !== null,
  );
}

export function useFocusTrap(
  active: boolean,
  containerRef: RefObject<HTMLElement | null>,
  initialFocus: InitialFocusTarget = 'first',
): void {
  useEffect(() => {
    if (!active || !containerRef.current) {
      return;
    }

    const container = containerRef.current;

    queueMicrotask(() => {
      if (!container.isConnected) {
        return;
      }

      if (typeof initialFocus === 'object' && initialFocus.current) {
        initialFocus.current.focus();
        return;
      }

      const focusables = getFocusableElements(container);
      if (focusables.length === 0) {
        container.tabIndex = -1;
        container.focus();
        return;
      }

      if (initialFocus === 'close') {
        const closeButton = container.querySelector<HTMLElement>('[aria-label="Close dialog"]');
        (closeButton ?? focusables[0]).focus();
        return;
      }

      focusables[0].focus();
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') {
        return;
      }

      const focusables = getFocusableElements(container);
      if (focusables.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', handleKeyDown);
    return () => container.removeEventListener('keydown', handleKeyDown);
  }, [active, containerRef, initialFocus]);
}
